import { useState, useRef, useEffect } from 'react';
import { useLang, LANG_LIST, Lang } from '@/hooks/useLang';

interface LangSwitcherProps {
  compact?: boolean;
}

export default function LangSwitcher({ compact = false }: LangSwitcherProps) {
  const { lang, setLang, langMeta } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (code: Lang) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap text-xs font-bold hover:scale-105"
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: 'white',
        }}
      >
        <span className="text-sm leading-none">{langMeta.flag}</span>
        {!compact && (
          <span className="hidden sm:inline">{langMeta.nativeLabel}</span>
        )}
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-[#1A0050]/98 backdrop-blur-xl border border-white/15 rounded-xl shadow-xl z-[10000] overflow-hidden">
          <div className="p-1.5">
            {LANG_LIST.map((l) => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all cursor-pointer text-left ${
                  lang === l.code
                    ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                    : 'text-white/70 hover:bg-white/8 hover:text-white'
                }`}
              >
                <span className="text-base leading-none w-5 text-center">{l.flag}</span>
                <span className="font-medium">{l.nativeLabel}</span>
                {lang === l.code && (
                  <i className="ri-check-line ml-auto text-[#F59E0B]"></i>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
